// useAuthStore.ts

import { auth } from "@/firebase";
import api from "@/services/api";
import authService from "@/services/authService"; // Import authService
import userInfoService from "@/services/userService";
import type { AccessRight, User } from "@/types/Login.types";
import { onAuthStateChanged } from "firebase/auth";
import { create } from "zustand";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  avatarUrl: string | null;

  initAuth: () => () => void;
  logout: () => Promise<void>;
  fetchAvatar: () => Promise<void>;
  setAvatarUrl: (url: string | null) => void;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: AccessRight) => boolean;
}

const retryRequest = async <T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise((resolve) => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 1.5);
  }
};

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isInitialized: false,
  avatarUrl: null,

  setAvatarUrl: (url) => {
    // Revoke old object URL if it exists to prevent memory leaks
    const currentUrl = get().avatarUrl;
    if (currentUrl && currentUrl.startsWith("blob:")) {
      URL.revokeObjectURL(currentUrl);
    }
    set({ avatarUrl: url });
  },

  fetchAvatar: async () => {
    try {
      const url = await userInfoService.getProfilePicUrl();
      get().setAvatarUrl(url);
    } catch (error) {
      console.log("No profile picture found.");
      console.log(error);
    }
  },

  initAuth: () => {
    if (get().isInitialized) {
      return () => {};
    }
    set({ isInitialized: true });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // 1. Forcefully retrieve a fresh Firebase ID token to prevent race conditions
          const token = await firebaseUser.getIdToken(true);
          // 2. Pass the token explicitly in the header for this initialization request
          const response = await retryRequest(
            async () => {
              return await api.get("/auth/me", {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
            },
            3,
            1000,
          ); // Retries 3 times, waiting 1s, then 1.5s, etc.

          // const response = await api.get("/auth/me");
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error("❌ /auth/me failed:", error);
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      } else {
        const currentUrl = get().avatarUrl;
        if (currentUrl && currentUrl.startsWith("blob:")) {
          URL.revokeObjectURL(currentUrl);
        }
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    });

    return unsubscribe;
  },

  logout: async () => {
    // Delegates full sign-out (Firebase + localStorage + API) to authService
    await authService.logout();
    set({ user: null, isAuthenticated: false, isLoading: false });
  },

  hasRole: (role) => {
    const user = get().user;
    if (!user || !user.role) return false;
    return Array.isArray(user.role) ? user.role.includes(role) : user.role === role;
  },

  hasPermission: (permission) => get().user?.accessRights?.includes(permission) ?? false,
}));
